import type { PasteFile } from "./api";
import { LIMITS, normalizePath, byteLength, toolResult } from "./tools";

export interface ToolContext {
  getFiles: () => PasteFile[];
  setFiles: (files: PasteFile[]) => void;
  getActivePath: () => string;
  /** Применить изменения: перечитать редактор, пометить dirty, обновить превью. */
  onChanged: () => void;
  executeJs: (code: string) => Promise<{ ok: boolean; value?: string; error?: string }>;
  getConsoleErrors: (limit: number) => Promise<Array<{ level: string; text: string }>>;
  getTitle: () => string;
  setTitle: (title: string) => void;
}

export interface ConsoleEntry {
  level: string;
  text: string;
}

const RESERVED = new Set([".vetchka"]);

/**
 * Исполняет инструмент модели над локальным состоянием редактора.
 * Возвращает текстовый результат, который уйдёт обратно модели.
 */
export async function runTool(
  name: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Promise<string> {
  switch (name) {
    case "list_files": {
      const files = ctx.getFiles();
      if (files.length === 0) return "(no files)";
      return files
        .map((f) => `${f.path} — ${byteLength(f.content)} b`)
        .join("\n");
    }

    case "read_file": {
      const path = normalizePath(String(args.path ?? ""));
      if (!path) return "error: invalid path";
      const file = ctx.getFiles().find((f) => f.path === path);
      if (!file) {
        return `error: no such file: ${path}. Available: ${ctx
          .getFiles()
          .map((f) => f.path)
          .join(", ")}`;
      }
      return toolResult(file.content === "" ? "(empty file)" : file.content);
    }

    case "write_file": {
      const path = normalizePath(String(args.path ?? ""));
      if (!path) return "error: invalid path (no '..', no leading '/')";
      if (RESERVED.has(path)) return `error: ${path} is reserved and cannot be written`;

      const content = String(args.content ?? "");
      const size = byteLength(content);
      if (size > LIMITS.maxFileBytes) {
        return `error: file too large (${size} > ${LIMITS.maxFileBytes} bytes)`;
      }

      const files = ctx.getFiles();
      const exists = files.some((f) => f.path === path);
      if (!exists && files.length >= LIMITS.maxFiles) {
        return `error: too many files (max ${LIMITS.maxFiles})`;
      }

      const total = files.reduce(
        (sum, f) => sum + (f.path === path ? 0 : byteLength(f.content)),
        size,
      );
      if (total > LIMITS.maxPasteBytes) {
        return `error: paste too large (${total} > ${LIMITS.maxPasteBytes} bytes)`;
      }

      const next = exists
        ? files.map((f) => (f.path === path ? { ...f, content } : f))
        : [...files, { path, content }];

      ctx.setFiles(next);
      ctx.onChanged();
      return `${exists ? "updated" : "created"} ${path} (${size} b)`;
    }

    case "delete_file": {
      const path = normalizePath(String(args.path ?? ""));
      if (!path) return "error: invalid path";
      if (RESERVED.has(path)) return `error: ${path} is reserved and cannot be deleted`;

      const files = ctx.getFiles();
      if (!files.some((f) => f.path === path)) return `error: no such file: ${path}`;
      if (files.length <= 1) return "error: cannot delete the last file";

      ctx.setFiles(files.filter((f) => f.path !== path));
      ctx.onChanged();
      return `deleted ${path}`;
    }

    case "get_console_errors": {
      const raw = Number(args.limit ?? 50);
      const limit = Number.isFinite(raw) ? Math.min(200, Math.max(1, Math.trunc(raw))) : 50;
      const entries = await ctx.getConsoleErrors(limit);
      if (entries.length === 0) return "(no console errors)";
      return toolResult(
        entries.map((e) => `[${e.level}] ${e.text}`).join("\n"),
      );
    }

    case "set_title": {
      const title = String(args.title ?? "").trim();
      if (title === "") return "error: title must not be empty";
      if (title.length > 200) return "error: title is too long (max 200)";
      ctx.setTitle(title);
      return `title set to "${title}"`;
    }

    case "execute_js": {
      const code = String(args.code ?? "");
      if (code.trim() === "") return "error: empty code";
      const res = await ctx.executeJs(code);
      if (!res.ok) return `error: ${res.error ?? "unknown error"}`;
      return toolResult(res.value === undefined ? "(undefined)" : res.value);
    }

    default:
      return `error: unknown tool: ${name}`;
  }
}